import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Complete translations for EN, FR, ES, DE
const translations = {
  en: {
    // Landing
    landing_title: "Play & Get Smarter",
    landing_subtitle: "Master one powerful concept per day. 5 minutes to unlock your brain's full potential.",
    get_started: "Get Started",
    already_have_account: "Already have an account?",
    sign_in: "Sign In",
    subscribe_prompt: "Subscribe to access all features",
    
    // Landing features
    social_proof: "Loved by 100,000+ learners worldwide",
    feature_brain_training: "5 min/day brain training",
    feature_streaks: "Build learning streaks",
    feature_simo: "Earn SIMO currency",
    feature_compete: "Compete globally",
    
    // Auth
    create_account: "Create Account",
    welcome_back: "Welcome Back",
    email: "Email",
    password: "Password",
    name: "Your Name",
    register: "Create Account",
    login: "Login",
    or_continue_with: "Or continue with",
    no_account: "Don't have an account?",
    have_account: "Already have an account?",
    start_journey: "Start your journey to genius",
    continue_journey: "Continue your brain training",
    
    // Onboarding
    onboarding_title: "Let's personalize your experience",
    age_group: "What's your age group?",
    learning_goal: "What do you want to master?",
    preferred_language: "Preferred language",
    reading_mode: "How do you prefer to learn?",
    read_mode: "Read",
    listen_mode: "Listen",
    parent_mode: "Enable Parent Mode",
    parent_mode_desc: "Track your child's progress and manage SIMO rewards",
    continue_btn: "Continue",
    
    // Age groups
    age_3_9: "Young Explorer (3-9)",
    age_10_plus: "Deep Thinker (10+)",
    
    // Goals
    goal_smarter: "Smarter Thinking",
    goal_science: "Science & Discovery",
    goal_vocabulary: "Vocabulary Power",
    goal_critical: "Critical Thinking",
    goal_memory: "Memory Enhancement",
    goal_genius: "Genius Reasoning",
    
    // Dashboard
    good_morning: "Good morning",
    good_afternoon: "Good afternoon",
    good_evening: "Good evening",
    todays_neurolex: "Today's NEUROLEX",
    one_per_day: "1 concept per day • Master it!",
    start_session: "Start Today's Challenge",
    your_streak: "Streak",
    simo_balance: "SIMO Balance",
    level: "Level",
    days: "days",
    already_completed: "Today's NEUROLEX Completed!",
    come_back_tomorrow: "Come back tomorrow for a new concept",
    streak_warning: "Don't break your streak! You'll lose SIMO!",
    
    // SIMO Currency
    simo_currency: "SIMO",
    simo_earned: "SIMO earned",
    simo_lost: "SIMO lost",
    simo_balance_label: "Your SIMO Bank",
    negative_balance: "You owe SIMO!",
    interest_warning: "10% daily interest applies",
    streak_penalty: "Streak broken! Lost",
    borrow_simo: "Borrow SIMO",
    lend_simo: "Lend SIMO",
    simo_exchange: "SIMO Exchange",
    simo_explanation: "SIMO is your brain currency! Earn it by learning. Spend it on real rewards at home!",
    
    // Session
    concept_of_day: "Concept of the Day",
    listen_to_lesson: "Listen",
    next: "Continue Learning",
    take_quiz: "Test Your Knowledge",
    professor_says: "Professor Simo says...",
    lets_explore: "Let's explore this fascinating concept together!",
    
    // Quiz
    quiz_title: "Brain Challenge",
    submit_answer: "Lock In Answer",
    correct: "Brilliant!",
    incorrect: "Almost there!",
    correct_answer_is: "The answer was:",
    simo_earned_quiz: "SIMO earned",
    continue_to_teach_back: "Now Teach It Back!",
    perfect_score: "Perfect! You're a genius!",
    
    // Teach Back
    teach_back_title: "Become the Teacher!",
    teach_back_instruction: "Explain what you learned in your own words. This is how your brain truly masters concepts!",
    type_explanation: "Type your explanation here...",
    record_voice: "Or record your voice",
    recording: "Recording...",
    stop_recording: "Stop Recording",
    submit_explanation: "Submit My Teaching",
    professor_feedback: "Professor Simo's feedback:",
    great_teaching: "Excellent teaching! Your brain just grew stronger!",
    
    // Recall
    recall_title: "Evening Brain Check",
    recall_instruction: "Let's see if today's concept stuck in your memory!",
    recall_warning: "Answer correctly for maximum SIMO reward!",
    view_lesson: "Need a hint? (Reduces SIMO)",
    
    // Next NEUROLEX
    next_neurolex_tomorrow: "Your next NEUROLEX arrives tomorrow morning!",
    morning_release_hint: "New concept drops at 6 AM your time - do it before school!",
    evening_recall_hint: "Tonight we'll test if you remember today's concept - without peeking!",
    
    // Progress
    progress_title: "Your Brain Journey",
    streak_history: "Streak Calendar",
    concepts_learned: "Concepts Mastered",
    quiz_accuracy: "Quiz Accuracy",
    total_simo: "Total SIMO Earned",
    days_learned: "Days of Learning",
    
    // Library
    library_title: "Concept Library",
    library_locked: "Upgrade to unlock the full library",
    library_info: "Tap on completed concepts to review them",
    library_empty: "No concepts available yet",
    completed: "Mastered",
    locked: "Locked",
    concepts_for_age: "Concepts for your age group",
    unlock_all: "Unlock All",
    
    // Concept Review
    review_concept: "Review Concept",
    back_to_library: "Library",
    concept_not_found: "Concept not found",
    concept_reviewed: "You have already completed this concept. This is a review.",
    no_content: "No content available for this section.",
    
    // Leaderboard
    leaderboard_title: "Champions Board",
    your_rank: "Your Rank",
    top_players: "Top Brain Masters",
    weekly_champion: "Weekly Champion",
    
    // Achievements
    achievements_title: "Achievements",
    unlocked: "Unlocked",
    locked_achievement: "Keep playing to unlock!",
    share_achievement: "Share Your Glory",
    
    // Settings
    settings_title: "Settings",
    account: "Account",
    language: "Language",
    notifications: "Notifications",
    morning_reminder: "Morning reminder",
    evening_recall: "Evening recall",
    age_settings: "Age group",
    reading_settings: "Learning mode",
    parent_settings: "Parent mode",
    logout: "Log Out",
    delete_account: "Delete Account",
    sound_effects: "Sound Effects",
    
    // Premium
    premium_title: "Unlock Full Power",
    premium_subtitle: "Continue your brain training journey",
    unlock_premium_features: "Unlock audio, voice & more",
    premium_feature_1: "Unlimited concept library",
    premium_feature_2: "Voice lessons with Professor Simo",
    premium_feature_3: "Voice teach-back",
    premium_feature_4: "Priority support",
    premium_feature_5: "Exclusive concepts",
    trial_ended: "Subscription required",
    trial_days_left: "Subscribe to continue",
    monthly: "Monthly",
    six_months: "6 Months",
    annual: "Annual",
    lifetime: "Lifetime",
    best_value: "Best Value",
    subscribe: "Subscribe Now",
    restore_purchase: "Restore Purchase",
    upgrade_now: "Upgrade Now",
    unlock_premium: "Unlock Premium",
    
    // Certificate
    certificate_title: "Daily Achievement",
    i_learned: "Today I mastered",
    share_certificate: "Share My Achievement",
    download_certificate: "Download Card",
    challenge_friends: "Challenge your friends!",
    click_to_play: "Click to play NEUROLEX",
    
    // Leaderboard & Weekly Competition
    all_time: "All Time",
    weekly: "Weekly",
    weekly_competition: "Weekly Competition",
    earn_most_simo: "Earn the most SIMO this week to win!",
    time_remaining: "remaining",
    last_week_winners: "Last Week's Winners",
    this_week: "This Week",
    no_weekly_data: "No competition data yet",
    top_players: "Top Players",
    
    // Common
    loading: "Loading...",
    error: "Error",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    done: "Done",
    skip: "Skip",
    back: "Back",
    close: "Close",
    share: "Share",
    
    // Professor Simo
    professor_intro: "Hello! I'm Professor Simo, your guide to becoming smarter every day!",
    professor_encourage: "Fantastic job! Your brain is getting stronger!",
    professor_correct: "Brilliant! You've just created new neural pathways!",
    professor_wrong: "Don't worry! Making mistakes is how geniuses learn!",
    professor_streak: "Amazing streak! Keep it going to earn more SIMO!",
    
    // Audio/Listen
    listen_full_concept: "Listen to Full Concept",
    listen_loading: "Loading audio...",
    pause_audio: "Pause Audio",
    tap_to_pause: "Tap to pause",
    prof_simo_reads: "Professor Simo reads everything",
    read_carefully: "Read carefully - you'll teach this back!",
    audio_reward_unlocked: "Audio Unlocked!",
    listen_professor_simo: "Listen to Professor Simo explain",
    
    // Session steps
    concept: "Concept",
    example: "Example",
    analogy: "Analogy",
    
    // Sound/Effects
    sound_correct: "Correct answer!",
    sound_wrong: "Try again!",
    sound_level_up: "Level up!",
    sound_achievement: "Achievement unlocked!",
    
    // Notifications
    notif_morning_title: "Your brain is waiting!",
    notif_morning_body: "Today's NEUROLEX concept is ready. Don't break your streak!",
    notif_evening_title: "Evening Brain Check",
    notif_evening_body: "Quick recall quiz ready! Test what you learned today.",
    
    // Referral
    refer_earn: "Refer & Earn",
    refer_subtitle: "Get 1 month Premium FREE!",
    
    // Session Error State
    session_load_error: "Unable to load session",
    session_error_message: "Something went wrong. Please try again.",
    try_again: "Try Again",
    back_to_dashboard: "Back to Dashboard",
    loading_session: "Loading your session...",
  },
  
  fr: {
    // Landing
    landing_title: "Joue et Deviens Génial",
    landing_subtitle: "Maîtrise un concept puissant par jour. 5 minutes pour libérer tout le potentiel de ton cerveau.",
    get_started: "Commencer",
    already_have_account: "Tu as déjà un compte?",
    sign_in: "Se Connecter",
    subscribe_prompt: "Abonnez-vous pour toutes les fonctionnalites",
    
    // Auth
    create_account: "Créer un Compte",
    welcome_back: "Bon Retour",
    email: "Email",
    password: "Mot de passe",
    name: "Ton Nom",
    register: "Créer un Compte",
    login: "Connexion",
    or_continue_with: "Ou continuer avec",
    no_account: "Tu n'as pas de compte?",
    have_account: "Tu as déjà un compte?",
    start_journey: "Commence ton voyage vers le génie",
    continue_journey: "Continue ton entraînement cérébral",
    
    // Onboarding
    onboarding_title: "Personnalisons ton expérience",
    age_group: "Quel est ton groupe d'âge?",
    learning_goal: "Que veux-tu maîtriser?",
    preferred_language: "Langue préférée",
    reading_mode: "Comment préfères-tu apprendre?",
    read_mode: "Lire",
    listen_mode: "Écouter",
    parent_mode: "Mode Parent",
    parent_mode_desc: "Suis les progrès de ton enfant et gère les récompenses SIMO",
    continue_btn: "Continuer",
    
    // Age groups
    age_3_9: "Jeune Explorateur (3-9)",
    age_10_plus: "Penseur Profond (10+)",
    
    // Goals
    goal_smarter: "Pensée Plus Intelligente",
    goal_science: "Science et Découverte",
    goal_vocabulary: "Puissance Vocabulaire",
    goal_critical: "Pensée Critique",
    goal_memory: "Amélioration Mémoire",
    goal_genius: "Raisonnement de Génie",
    
    // Dashboard
    good_morning: "Bonjour",
    good_afternoon: "Bon après-midi",
    good_evening: "Bonsoir",
    todays_neurolex: "NEUROLEX du Jour",
    one_per_day: "1 concept par jour • Maîtrise-le!",
    start_session: "Relever le Défi du Jour",
    your_streak: "Série",
    simo_balance: "Solde SIMO",
    level: "Niveau",
    days: "jours",
    already_completed: "NEUROLEX du Jour Complété!",
    come_back_tomorrow: "Reviens demain pour un nouveau concept",
    streak_warning: "Ne brise pas ta série! Tu perdras des SIMO!",
    
    // SIMO Currency
    simo_currency: "SIMO",
    simo_earned: "SIMO gagnés",
    simo_lost: "SIMO perdus",
    simo_balance_label: "Ta Banque SIMO",
    negative_balance: "Tu dois des SIMO!",
    interest_warning: "Intérêt de 10% par jour appliqué",
    streak_penalty: "Série brisée! Perdu",
    borrow_simo: "Emprunter des SIMO",
    lend_simo: "Prêter des SIMO",
    simo_exchange: "Échange SIMO",
    simo_explanation: "SIMO est ta monnaie cérébrale! Gagne-la en apprenant. Dépense-la pour de vraies récompenses à la maison!",
    
    // Session
    concept_of_day: "Concept du Jour",
    listen_to_lesson: "Écouter",
    next: "Continuer à Apprendre",
    take_quiz: "Tester Tes Connaissances",
    professor_says: "Professeur Simo dit...",
    lets_explore: "Explorons ensemble ce concept fascinant!",
    
    // Quiz
    quiz_title: "Défi Cérébral",
    submit_answer: "Valider la Réponse",
    correct: "Génial!",
    incorrect: "Presque!",
    correct_answer_is: "La réponse était:",
    simo_earned_quiz: "SIMO gagnés",
    continue_to_teach_back: "Maintenant, Enseigne-le!",
    perfect_score: "Parfait! Tu es un génie!",
    
    // Teach Back
    teach_back_title: "Deviens le Professeur!",
    teach_back_instruction: "Explique ce que tu as appris avec tes propres mots. C'est ainsi que ton cerveau maîtrise vraiment les concepts!",
    type_explanation: "Tape ton explication ici...",
    record_voice: "Ou enregistre ta voix",
    recording: "Enregistrement...",
    stop_recording: "Arrêter l'Enregistrement",
    submit_explanation: "Soumettre Mon Enseignement",
    professor_feedback: "Feedback du Professeur Simo:",
    great_teaching: "Excellent enseignement! Ton cerveau vient de se renforcer!",
    
    // Recall
    recall_title: "Vérification Cérébrale du Soir",
    recall_instruction: "Voyons si le concept d'aujourd'hui est resté dans ta mémoire!",
    recall_warning: "Réponds correctement pour un maximum de SIMO!",
    view_lesson: "Besoin d'un indice? (Réduit les SIMO)",
    
    // Next NEUROLEX
    next_neurolex_tomorrow: "Ton prochain NEUROLEX arrive demain matin!",
    morning_release_hint: "Nouveau concept à 6h - fais-le avant l'école!",
    evening_recall_hint: "Ce soir on teste si tu te souviens du concept - sans tricher!",
    
    // Progress
    progress_title: "Ton Voyage Cérébral",
    streak_history: "Calendrier des Séries",
    concepts_learned: "Concepts Maîtrisés",
    quiz_accuracy: "Précision aux Quiz",
    total_simo: "Total SIMO Gagnés",
    days_learned: "Jours d'Apprentissage",
    
    // Library
    library_title: "Bibliothèque des Concepts",
    library_locked: "Passe à Premium pour débloquer toute la bibliothèque",
    library_info: "Appuie sur les concepts terminés pour les réviser",
    library_empty: "Aucun concept disponible pour le moment",
    completed: "Maîtrisé",
    locked: "Verrouillé",
    concepts_for_age: "Concepts pour ton groupe d'âge",
    unlock_all: "Tout Débloquer",
    
    // Concept Review
    review_concept: "Réviser le Concept",
    back_to_library: "Bibliothèque",
    concept_not_found: "Concept introuvable",
    concept_reviewed: "Tu as déjà terminé ce concept. Ceci est une révision.",
    no_content: "Aucun contenu disponible pour cette section.",
    
    // Leaderboard
    leaderboard_title: "Tableau des Champions",
    your_rank: "Ton Classement",
    top_players: "Meilleurs Maîtres du Cerveau",
    weekly_champion: "Champion de la Semaine",
    
    // Achievements
    achievements_title: "Succès",
    unlocked: "Débloqué",
    locked_achievement: "Continue à jouer pour débloquer!",
    share_achievement: "Partager Ta Gloire",
    
    // Settings
    settings_title: "Paramètres",
    account: "Compte",
    language: "Langue",
    notifications: "Notifications",
    morning_reminder: "Rappel du matin",
    evening_recall: "Rappel du soir",
    age_settings: "Groupe d'âge",
    reading_settings: "Mode d'apprentissage",
    parent_settings: "Mode parent",
    logout: "Déconnexion",
    delete_account: "Supprimer le Compte",
    sound_effects: "Effets Sonores",
    
    // Premium
    premium_title: "Débloque Tout le Pouvoir",
    premium_subtitle: "Continue ton voyage d'entraînement cérébral",
    unlock_premium_features: "Débloque l'audio, la voix et plus",
    premium_feature_1: "Bibliothèque de concepts illimitée",
    premium_feature_2: "Leçons vocales avec Professeur Simo",
    premium_feature_3: "Retour d'enseignement vocal",
    premium_feature_4: "Support prioritaire",
    premium_feature_5: "Concepts exclusifs",
    trial_ended: "Abonnement requis",
    trial_days_left: "Abonnez-vous pour continuer",
    monthly: "Mensuel",
    six_months: "6 Mois",
    annual: "Annuel",
    lifetime: "À Vie",
    best_value: "Meilleure Valeur",
    subscribe: "S'abonner Maintenant",
    restore_purchase: "Restaurer l'Achat",
    upgrade_now: "Passer à Premium",
    unlock_premium: "Débloquer Premium",
    
    // Certificate
    certificate_title: "Réussite du Jour",
    i_learned: "Aujourd'hui j'ai maîtrisé",
    share_certificate: "Partager Ma Réussite",
    download_certificate: "Télécharger la Carte",
    challenge_friends: "Défie tes amis!",
    click_to_play: "Clique pour jouer à NEUROLEX",
    
    // Common
    loading: "Chargement...",
    error: "Erreur",
    retry: "Réessayer",
    cancel: "Annuler",
    save: "Sauvegarder",
    done: "Terminé",
    skip: "Passer",
    back: "Retour",
    close: "Fermer",
    share: "Partager",
    
    // Professor Simo
    professor_intro: "Salut! Je suis Professeur Simo, ton guide pour devenir plus intelligent chaque jour!",
    professor_encourage: "Travail fantastique! Ton cerveau devient plus fort!",
    professor_correct: "Brillant! Tu viens de créer de nouvelles connexions neuronales!",
    professor_wrong: "Ne t'inquiète pas! C'est en faisant des erreurs qu'on devient un génie!",
    professor_streak: "Série incroyable! Continue pour gagner plus de SIMO!",
    
    // Audio/Listen
    listen_full_concept: "Écouter le Concept Complet",
    listen_loading: "Chargement audio...",
    pause_audio: "Pause Audio",
    tap_to_pause: "Touche pour pause",
    prof_simo_reads: "Professeur Simo lit tout",
    read_carefully: "Lis attentivement - tu devras expliquer!",
    audio_reward_unlocked: "Audio Débloqué!",
    listen_professor_simo: "Écoute le Professeur Simo",
    
    // Session steps
    concept: "Concept",
    example: "Exemple",
    analogy: "Analogie",
    
    // Notifications
    notif_morning_title: "Ton cerveau t'attend!",
    notif_morning_body: "Le concept NEUROLEX du jour est prêt. Ne brise pas ta série!",
    notif_evening_title: "Vérification Cérébrale du Soir",
    notif_evening_body: "Quiz de rappel rapide prêt! Teste ce que tu as appris aujourd'hui.",
    
    // Referral
    refer_earn: "Parraine et gagne",
    refer_subtitle: "Obtiens 1 mois Premium GRATUIT!",
    
    // Session Error State
    session_load_error: "Impossible de charger la session",
    session_error_message: "Une erreur s'est produite. Veuillez réessayer.",
    try_again: "Réessayer",
    back_to_dashboard: "Retour au tableau de bord",
    loading_session: "Chargement de ta session...",
  },
  
  es: {
    // Landing
    landing_title: "Juega y Vuélvete Genial",
    landing_subtitle: "Domina un concepto poderoso por día. 5 minutos para desbloquear todo el potencial de tu cerebro.",
    get_started: "Comenzar",
    already_have_account: "¿Ya tienes una cuenta?",
    sign_in: "Iniciar Sesión",
    subscribe_prompt: "Suscribete para acceder a todas las funciones",
    
    // Auth
    create_account: "Crear Cuenta",
    welcome_back: "Bienvenido de Nuevo",
    email: "Correo electrónico",
    password: "Contraseña",
    name: "Tu Nombre",
    register: "Crear Cuenta",
    login: "Iniciar Sesión",
    or_continue_with: "O continuar con",
    no_account: "¿No tienes una cuenta?",
    have_account: "¿Ya tienes una cuenta?",
    start_journey: "Comienza tu viaje hacia la genialidad",
    continue_journey: "Continúa tu entrenamiento cerebral",
    
    // Onboarding
    onboarding_title: "Personalicemos tu experiencia",
    age_group: "¿Cuál es tu grupo de edad?",
    learning_goal: "¿Qué quieres dominar?",
    preferred_language: "Idioma preferido",
    reading_mode: "¿Cómo prefieres aprender?",
    read_mode: "Leer",
    listen_mode: "Escuchar",
    parent_mode: "Modo Padres",
    parent_mode_desc: "Rastrea el progreso de tu hijo y gestiona las recompensas SIMO",
    continue_btn: "Continuar",
    
    // Age groups
    age_3_9: "Joven Explorador (3-9)",
    age_10_plus: "Pensador Profundo (10+)",
    
    // Goals
    goal_smarter: "Pensamiento Más Inteligente",
    goal_science: "Ciencia y Descubrimiento",
    goal_vocabulary: "Poder del Vocabulario",
    goal_critical: "Pensamiento Crítico",
    goal_memory: "Mejora de Memoria",
    goal_genius: "Razonamiento de Genio",
    
    // Dashboard
    good_morning: "Buenos días",
    good_afternoon: "Buenas tardes",
    good_evening: "Buenas noches",
    todays_neurolex: "NEUROLEX de Hoy",
    one_per_day: "1 concepto por día • ¡Domínalo!",
    start_session: "Comenzar el Desafío de Hoy",
    your_streak: "Racha",
    simo_balance: "Saldo SIMO",
    level: "Nivel",
    days: "días",
    already_completed: "¡NEUROLEX de Hoy Completado!",
    come_back_tomorrow: "Vuelve mañana para un nuevo concepto",
    streak_warning: "¡No rompas tu racha! ¡Perderás SIMO!",
    
    // SIMO Currency
    simo_currency: "SIMO",
    simo_earned: "SIMO ganados",
    simo_lost: "SIMO perdidos",
    simo_balance_label: "Tu Banco SIMO",
    negative_balance: "¡Debes SIMO!",
    interest_warning: "Se aplica 10% de interés diario",
    streak_penalty: "¡Racha rota! Perdido",
    borrow_simo: "Pedir SIMO prestados",
    lend_simo: "Prestar SIMO",
    simo_exchange: "Intercambio SIMO",
    simo_explanation: "¡SIMO es tu moneda cerebral! Gánala aprendiendo. ¡Gástala en recompensas reales en casa!",
    
    // Session
    concept_of_day: "Concepto del Día",
    listen_to_lesson: "Escuchar",
    next: "Seguir Aprendiendo",
    take_quiz: "Probar Tus Conocimientos",
    professor_says: "El Profesor Simo dice...",
    lets_explore: "¡Exploremos juntos este concepto fascinante!",
    
    // Quiz
    quiz_title: "Desafío Cerebral",
    submit_answer: "Confirmar Respuesta",
    correct: "¡Brillante!",
    incorrect: "¡Casi!",
    correct_answer_is: "La respuesta era:",
    simo_earned_quiz: "SIMO ganados",
    continue_to_teach_back: "¡Ahora Enséñalo!",
    perfect_score: "¡Perfecto! ¡Eres un genio!",
    
    // Teach Back
    teach_back_title: "¡Conviértete en el Profesor!",
    teach_back_instruction: "Explica lo que aprendiste con tus propias palabras. ¡Así es como tu cerebro realmente domina los conceptos!",
    type_explanation: "Escribe tu explicación aquí...",
    record_voice: "O graba tu voz",
    recording: "Grabando...",
    stop_recording: "Detener Grabación",
    submit_explanation: "Enviar Mi Enseñanza",
    professor_feedback: "Comentarios del Profesor Simo:",
    great_teaching: "¡Excelente enseñanza! ¡Tu cerebro se ha fortalecido!",
    
    // Recall
    recall_title: "Verificación Cerebral Nocturna",
    recall_instruction: "¡Veamos si el concepto de hoy se quedó en tu memoria!",
    recall_warning: "¡Responde correctamente para máximo SIMO!",
    view_lesson: "¿Necesitas una pista? (Reduce SIMO)",
    
    // Next NEUROLEX
    next_neurolex_tomorrow: "¡Tu próximo NEUROLEX llega mañana por la mañana!",
    morning_release_hint: "Nuevo concepto a las 6 AM - ¡hazlo antes de ir a la escuela!",
    evening_recall_hint: "Esta noche probaremos si recuerdas el concepto - ¡sin mirar!",
    
    // Progress
    progress_title: "Tu Viaje Cerebral",
    streak_history: "Calendario de Rachas",
    concepts_learned: "Conceptos Dominados",
    quiz_accuracy: "Precisión en Quiz",
    total_simo: "Total SIMO Ganados",
    days_learned: "Días de Aprendizaje",
    
    // Library
    library_title: "Biblioteca de Conceptos",
    library_locked: "Actualiza a Premium para desbloquear toda la biblioteca",
    library_info: "Toca los conceptos completados para revisarlos",
    library_empty: "No hay conceptos disponibles aún",
    completed: "Dominado",
    locked: "Bloqueado",
    concepts_for_age: "Conceptos para tu grupo de edad",
    unlock_all: "Desbloquear Todo",
    
    // Concept Review
    review_concept: "Revisar Concepto",
    back_to_library: "Biblioteca",
    concept_not_found: "Concepto no encontrado",
    concept_reviewed: "Ya has completado este concepto. Esto es una revisión.",
    no_content: "No hay contenido disponible para esta sección.",
    
    // Leaderboard
    leaderboard_title: "Tabla de Campeones",
    your_rank: "Tu Clasificación",
    top_players: "Mejores Maestros del Cerebro",
    weekly_champion: "Campeón de la Semana",
    
    // Achievements
    achievements_title: "Logros",
    unlocked: "Desbloqueado",
    locked_achievement: "¡Sigue jugando para desbloquear!",
    share_achievement: "Compartir Tu Gloria",
    
    // Settings
    settings_title: "Configuración",
    account: "Cuenta",
    language: "Idioma",
    notifications: "Notificaciones",
    morning_reminder: "Recordatorio matutino",
    evening_recall: "Recordatorio nocturno",
    age_settings: "Grupo de edad",
    reading_settings: "Modo de aprendizaje",
    parent_settings: "Modo padres",
    logout: "Cerrar Sesión",
    delete_account: "Eliminar Cuenta",
    sound_effects: "Efectos de Sonido",
    
    // Premium
    premium_title: "Desbloquea Todo el Poder",
    premium_subtitle: "Continúa tu viaje de entrenamiento cerebral",
    premium_feature_1: "Biblioteca de conceptos ilimitada",
    premium_feature_2: "Lecciones de voz con Profesor Simo",
    premium_feature_3: "Enseñanza por voz",
    premium_feature_4: "Soporte prioritario",
    premium_feature_5: "Conceptos exclusivos",
    trial_ended: "Suscripcion requerida",
    trial_days_left: "Suscribete para continuar",
    monthly: "Mensual",
    six_months: "6 Meses",
    annual: "Anual",
    lifetime: "De por Vida",
    best_value: "Mejor Valor",
    subscribe: "Suscribirse Ahora",
    restore_purchase: "Restaurar Compra",
    upgrade_now: "Actualizar Ahora",
    unlock_premium: "Desbloquear Premium",
    
    // Certificate
    certificate_title: "Logro del Día",
    i_learned: "Hoy dominé",
    share_certificate: "Compartir Mi Logro",
    download_certificate: "Descargar Tarjeta",
    challenge_friends: "¡Desafía a tus amigos!",
    click_to_play: "Haz clic para jugar NEUROLEX",
    
    // Common
    loading: "Cargando...",
    error: "Error",
    retry: "Reintentar",
    cancel: "Cancelar",
    save: "Guardar",
    done: "Hecho",
    skip: "Saltar",
    back: "Volver",
    close: "Cerrar",
    share: "Compartir",
    
    // Professor Simo
    professor_intro: "¡Hola! ¡Soy el Profesor Simo, tu guía para ser más inteligente cada día!",
    professor_encourage: "¡Trabajo fantástico! ¡Tu cerebro se está fortaleciendo!",
    professor_correct: "¡Brillante! ¡Acabas de crear nuevas conexiones neuronales!",
    professor_wrong: "¡No te preocupes! ¡Cometer errores es como los genios aprenden!",
    professor_streak: "¡Racha increíble! ¡Sigue para ganar más SIMO!",
    
    // Audio/Listen
    listen_full_concept: "Escuchar Concepto Completo",
    listen_loading: "Cargando audio...",
    pause_audio: "Pausar Audio",
    tap_to_pause: "Toca para pausar",
    prof_simo_reads: "El Profesor Simo lee todo",
    read_carefully: "¡Lee con atención - lo enseñarás después!",
    audio_reward_unlocked: "¡Audio Desbloqueado!",
    listen_professor_simo: "Escucha al Profesor Simo",
    
    // Session steps
    concept: "Concepto",
    example: "Ejemplo",
    analogy: "Analogía",
    
    // Notifications
    notif_morning_title: "¡Tu cerebro te espera!",
    notif_morning_body: "El concepto NEUROLEX de hoy está listo. ¡No rompas tu racha!",
    notif_evening_title: "Verificación Cerebral Nocturna",
    notif_evening_body: "¡Quiz de repaso rápido listo! Prueba lo que aprendiste hoy.",
    
    // Referral
    refer_earn: "Recomienda y gana",
    refer_subtitle: "¡Obtén 1 mes Premium GRATIS!",
    
    // Session Error State
    session_load_error: "No se pudo cargar la sesión",
    session_error_message: "Algo salió mal. Por favor, inténtalo de nuevo.",
    try_again: "Intentar de Nuevo",
    back_to_dashboard: "Volver al Panel",
    loading_session: "Cargando tu sesión...",
  },
  
  de: {
    // Landing
    landing_title: "Spiele & Werde Genial",
    landing_subtitle: "Meistere jeden Tag ein mächtiges Konzept. 5 Minuten, um das volle Potenzial deines Gehirns zu entfesseln.",
    get_started: "Jetzt Starten",
    already_have_account: "Hast du schon ein Konto?",
    sign_in: "Anmelden",
    subscribe_prompt: "Abonnieren Sie fur alle Funktionen",
    
    // Auth
    create_account: "Konto Erstellen",
    welcome_back: "Willkommen Zurück",
    email: "E-Mail",
    password: "Passwort",
    name: "Dein Name",
    register: "Konto Erstellen",
    login: "Anmelden",
    or_continue_with: "Oder fortfahren mit",
    no_account: "Hast du noch kein Konto?",
    have_account: "Hast du schon ein Konto?",
    start_journey: "Starte deine Reise zum Genie",
    continue_journey: "Setze dein Gehirntraining fort",
    
    // Onboarding
    onboarding_title: "Lass uns deine Erfahrung personalisieren",
    age_group: "Was ist deine Altersgruppe?",
    learning_goal: "Was möchtest du meistern?",
    preferred_language: "Bevorzugte Sprache",
    reading_mode: "Wie lernst du am liebsten?",
    read_mode: "Lesen",
    listen_mode: "Hören",
    parent_mode: "Elternmodus",
    parent_mode_desc: "Verfolge den Fortschritt deines Kindes und verwalte SIMO-Belohnungen",
    continue_btn: "Weiter",
    
    // Age groups
    age_3_9: "Junger Entdecker (3-9)",
    age_10_plus: "Tiefer Denker (10+)",
    
    // Goals
    goal_smarter: "Intelligenteres Denken",
    goal_science: "Wissenschaft & Entdeckung",
    goal_vocabulary: "Wortschatz-Power",
    goal_critical: "Kritisches Denken",
    goal_memory: "Gedächtnisverbesserung",
    goal_genius: "Genie-Denken",
    
    // Dashboard
    good_morning: "Guten Morgen",
    good_afternoon: "Guten Tag",
    good_evening: "Guten Abend",
    todays_neurolex: "Heutiges NEUROLEX",
    one_per_day: "1 Konzept pro Tag • Meistere es!",
    start_session: "Heutige Herausforderung Starten",
    your_streak: "Serie",
    simo_balance: "SIMO-Guthaben",
    level: "Level",
    days: "Tage",
    already_completed: "Heutiges NEUROLEX Abgeschlossen!",
    come_back_tomorrow: "Komm morgen für ein neues Konzept zurück",
    streak_warning: "Brich deine Serie nicht! Du verlierst SIMO!",
    
    // SIMO Currency
    simo_currency: "SIMO",
    simo_earned: "SIMO verdient",
    simo_lost: "SIMO verloren",
    simo_balance_label: "Deine SIMO-Bank",
    negative_balance: "Du schuldest SIMO!",
    interest_warning: "10% tägliche Zinsen werden angewendet",
    streak_penalty: "Serie gebrochen! Verloren",
    borrow_simo: "SIMO leihen",
    lend_simo: "SIMO verleihen",
    simo_exchange: "SIMO-Börse",
    simo_explanation: "SIMO ist deine Gehirnwährung! Verdiene sie durch Lernen. Gib sie für echte Belohnungen zu Hause aus!",
    
    // Session
    concept_of_day: "Konzept des Tages",
    listen_to_lesson: "Anhören",
    next: "Weiter Lernen",
    take_quiz: "Wissen Testen",
    professor_says: "Professor Simo sagt...",
    lets_explore: "Lass uns dieses faszinierende Konzept gemeinsam erkunden!",
    
    // Quiz
    quiz_title: "Gehirn-Challenge",
    submit_answer: "Antwort Bestätigen",
    correct: "Brillant!",
    incorrect: "Fast!",
    correct_answer_is: "Die Antwort war:",
    simo_earned_quiz: "SIMO verdient",
    continue_to_teach_back: "Jetzt Erkläre Es!",
    perfect_score: "Perfekt! Du bist ein Genie!",
    
    // Teach Back
    teach_back_title: "Werde zum Lehrer!",
    teach_back_instruction: "Erkläre, was du gelernt hast, mit deinen eigenen Worten. So meistert dein Gehirn wirklich Konzepte!",
    type_explanation: "Tippe deine Erklärung hier ein...",
    record_voice: "Oder nimm deine Stimme auf",
    recording: "Aufnahme...",
    stop_recording: "Aufnahme Stoppen",
    submit_explanation: "Meine Lehre Einreichen",
    professor_feedback: "Professor Simos Feedback:",
    great_teaching: "Hervorragendes Lehren! Dein Gehirn ist gerade stärker geworden!",
    
    // Recall
    recall_title: "Abend-Gehirncheck",
    recall_instruction: "Lass uns sehen, ob das heutige Konzept in deinem Gedächtnis geblieben ist!",
    recall_warning: "Antworte richtig für maximale SIMO!",
    view_lesson: "Brauchst du einen Hinweis? (Reduziert SIMO)",
    
    // Next NEUROLEX
    next_neurolex_tomorrow: "Dein nächstes NEUROLEX kommt morgen früh!",
    morning_release_hint: "Neues Konzept um 6 Uhr - mach es vor der Schule!",
    evening_recall_hint: "Heute Abend testen wir, ob du dich an das Konzept erinnerst - ohne zu spicken!",
    
    // Progress
    progress_title: "Deine Gehirn-Reise",
    streak_history: "Serien-Kalender",
    concepts_learned: "Gemeisterte Konzepte",
    quiz_accuracy: "Quiz-Genauigkeit",
    total_simo: "Gesamte SIMO verdient",
    days_learned: "Lerntage",
    
    // Library
    library_title: "Konzept-Bibliothek",
    library_locked: "Upgrade auf Premium, um die vollständige Bibliothek freizuschalten",
    library_info: "Tippe auf abgeschlossene Konzepte, um sie zu wiederholen",
    library_empty: "Noch keine Konzepte verfügbar",
    completed: "Gemeistert",
    locked: "Gesperrt",
    concepts_for_age: "Konzepte für deine Altersgruppe",
    unlock_all: "Alles Freischalten",
    
    // Concept Review
    review_concept: "Konzept Wiederholen",
    back_to_library: "Bibliothek",
    concept_not_found: "Konzept nicht gefunden",
    concept_reviewed: "Du hast dieses Konzept bereits abgeschlossen. Dies ist eine Wiederholung.",
    no_content: "Kein Inhalt für diesen Abschnitt verfügbar.",
    
    // Leaderboard
    leaderboard_title: "Champions-Tabelle",
    your_rank: "Dein Rang",
    top_players: "Top-Gehirnmeister",
    weekly_champion: "Wöchentlicher Champion",
    
    // Achievements
    achievements_title: "Erfolge",
    unlocked: "Freigeschaltet",
    locked_achievement: "Spiele weiter zum Freischalten!",
    share_achievement: "Teile Deinen Ruhm",
    
    // Settings
    settings_title: "Einstellungen",
    account: "Konto",
    language: "Sprache",
    notifications: "Benachrichtigungen",
    morning_reminder: "Morgen-Erinnerung",
    evening_recall: "Abend-Erinnerung",
    age_settings: "Altersgruppe",
    reading_settings: "Lernmodus",
    parent_settings: "Elternmodus",
    logout: "Abmelden",
    delete_account: "Konto Löschen",
    sound_effects: "Soundeffekte",
    
    // Premium
    premium_title: "Volle Power Freischalten",
    premium_subtitle: "Setze deine Gehirntraining-Reise fort",
    premium_feature_1: "Unbegrenzte Konzept-Bibliothek",
    premium_feature_2: "Sprach-Lektionen mit Professor Simo",
    premium_feature_3: "Sprach-Lehre",
    premium_feature_4: "Prioritäts-Support",
    premium_feature_5: "Exklusive Konzepte",
    trial_ended: "Abonnement erforderlich",
    trial_days_left: "Abonnieren Sie um fortzufahren",
    monthly: "Monatlich",
    six_months: "6 Monate",
    annual: "Jährlich",
    lifetime: "Lebenslang",
    best_value: "Bester Wert",
    subscribe: "Jetzt Abonnieren",
    restore_purchase: "Kauf Wiederherstellen",
    upgrade_now: "Jetzt Upgraden",
    unlock_premium: "Premium Freischalten",
    
    // Certificate
    certificate_title: "Tages-Erfolg",
    i_learned: "Heute habe ich gemeistert",
    share_certificate: "Meinen Erfolg Teilen",
    download_certificate: "Karte Herunterladen",
    challenge_friends: "Fordere deine Freunde heraus!",
    click_to_play: "Klicke zum NEUROLEX spielen",
    
    // Common
    loading: "Lädt...",
    error: "Fehler",
    retry: "Wiederholen",
    cancel: "Abbrechen",
    save: "Speichern",
    done: "Fertig",
    skip: "Überspringen",
    back: "Zurück",
    close: "Schließen",
    share: "Teilen",
    
    // Professor Simo
    professor_intro: "Hallo! Ich bin Professor Simo, dein Guide, um jeden Tag schlauer zu werden!",
    professor_encourage: "Fantastische Arbeit! Dein Gehirn wird stärker!",
    professor_correct: "Brillant! Du hast gerade neue neuronale Verbindungen geschaffen!",
    professor_wrong: "Keine Sorge! Fehler machen ist, wie Genies lernen!",
    professor_streak: "Unglaubliche Serie! Mach weiter, um mehr SIMO zu verdienen!",
    
    // Audio/Listen
    listen_full_concept: "Vollständiges Konzept Anhören",
    listen_loading: "Audio wird geladen...",
    pause_audio: "Audio Pausieren",
    tap_to_pause: "Tippe zum Pausieren",
    prof_simo_reads: "Professor Simo liest alles vor",
    
    // Session steps
    concept: "Konzept",
    example: "Beispiel",
    analogy: "Analogie",
    
    // Notifications
    notif_morning_title: "Dein Gehirn wartet!",
    notif_morning_body: "Das heutige NEUROLEX-Konzept ist bereit. Brich deine Serie nicht!",
    notif_evening_title: "Abend-Gehirncheck",
    notif_evening_body: "Schnelles Wiederholungsquiz bereit! Teste, was du heute gelernt hast.",
    
    // Referral
    refer_earn: "Empfehlen & Verdienen",
    refer_subtitle: "Erhalte 1 Monat Premium GRATIS!",
    
    // Session Error State
    session_load_error: "Sitzung konnte nicht geladen werden",
    session_error_message: "Etwas ist schief gelaufen. Bitte versuche es erneut.",
    try_again: "Erneut versuchen",
    back_to_dashboard: "Zurück zum Dashboard",
    loading_session: "Lade deine Sitzung...",
  },
};

export const useI18nStore = create(
  persist(
    (set, get) => ({
      language: 'en',
      
      setLanguage: (language) => set({ language }),
      
      // Sync language from user account (call this after login/loadUser)
      syncFromUser: (userLanguage) => {
        if (userLanguage && ['en', 'fr', 'es', 'de', 'it', 'pt', 'ar', 'zh', 'ja', 'ko'].includes(userLanguage)) {
          set({ language: userLanguage });
        }
      },
      
      t: (key) => {
        const { language } = get();
        return translations[language]?.[key] || translations.en[key] || key;
      },
      
      getLanguages: () => [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
      ],
    }),
    {
      name: 'neurolex-i18n',
    }
  )
);
